import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import { Button } from 'src/components/ui/button';
import DataTable from 'src/components/ui/data-table';
import { Plus, Pencil, UserPlus, Building2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { OrganizationService } from 'src/services/api/OrganizationService';
import {
  useCreateOrganization,
  useUpdateOrganization,
  useAssignRM,
} from 'src/services/api/mutations/organizationMutations';
import { useToast } from 'src/hooks/use-toast';
import { OrganizationModal } from 'src/components/Modals/OrganizationModal';
import { StatusBadge } from 'src/components/common/StatusBadge';
import { RMAssignmentModal } from 'src/components/Modals/RMAssignmentModal';

const StatusCell = ({ row }) => (
  <StatusBadge value={row.original.is_active ? 'ACTIVE' : 'INACTIVE'} />
);

const CustomerTypeCell = ({ value }) => (
  <span className="inline-flex items-center px-2 py-[3px] rounded-full text-[10.5px] font-semibold tracking-[0.04em] uppercase whitespace-nowrap bg-muted text-muted-foreground">
    {value}
  </span>
);

const RMCell = ({ row }) => {
  const value = row?.original?.rm_name;
  const trimmed = value?.trim();
  return trimmed ? (
    <span className="text-[12.5px] text-muted-foreground">{trimmed}</span>
  ) : (
    <span className="text-[12.5px] text-foreground-muted italic">
      Unassigned
    </span>
  );
};

const ActionsCell = ({ row, onEdit, onAssign }) => (
  <div className="flex items-center gap-1">
    <Button
      variant="ghost"
      size="icon"
      className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity w-7 h-7 rounded-md hover:bg-muted"
      title="Edit organization"
      onClick={(e) => {
        e.stopPropagation();
        onEdit(row.original.id);
      }}
    >
      <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
    </Button>
    <Button
      variant="ghost"
      size="icon"
      className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity w-7 h-7 rounded-md hover:bg-brand-bg-subtle hover:text-primary"
      title="Assign Relationship Manager"
      onClick={(e) => {
        e.stopPropagation();
        onAssign(row.original.id);
      }}
    >
      <UserPlus className="h-3.5 w-3.5 text-muted-foreground" />
    </Button>
  </div>
);

const createColumns = (handleEditClick, handleAssignClick) => [
  {
    key: 'name',
    header: 'Organization Name',
    minWidth: '220px',
    render: (v) => (
      <span className="text-[13px] font-[550] text-foreground-strong">{v}</span>
    ),
  },
  {
    key: 'description',
    header: 'Description',
    width: '280px',
    minWidth: '160px',
    render: (v) => (
      <div className="min-w-0 w-full">
        <span
          className="block text-[12.5px] text-muted-foreground truncate"
          title={v}
        >
          {v}
        </span>
      </div>
    ),
  },
  {
    key: 'customer_type',
    header: 'Customer Type',
    width: '150px',
    minWidth: '130px',
    render: (v) => <CustomerTypeCell value={v} />,
  },
  {
    key: 'rm_name',
    header: 'RM',
    width: '160px',
    minWidth: '140px',
    render: (_v, row) => <RMCell row={{ original: row }} />,
  },
  {
    key: 'is_active',
    header: 'Status',
    width: '120px',
    minWidth: '110px',
    render: (_v, row) => <StatusCell row={{ original: row }} />,
  },
  {
    key: 'actions',
    header: '',
    width: '80px',
    minWidth: '80px',
    render: (_v, row) => (
      <ActionsCell
        row={{ original: row }}
        onEdit={handleEditClick}
        onAssign={handleAssignClick}
      />
    ),
  },
];

const DEFAULT_PAGE_SIZE = 10;

function Organizations() {
  const { toast } = useToast();
  // Note: sorting removed; server-side pagination only in this view
  const [organizations, setOrganizations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // Get current page and page size from URL
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(
    searchParams.get('pageSize') || DEFAULT_PAGE_SIZE.toString(),
    10
  );
  const [totalItems, setTotalItems] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isRMModalOpen, setIsRMModalOpen] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState(null);

  // React Query mutations
  const createOrganizationMutation = useCreateOrganization();
  const updateOrganizationMutation = useUpdateOrganization();
  const assignRMMutation = useAssignRM();

  const handleAssignClick = (orgId) => {
    setSelectedOrgId(orgId);
    setIsRMModalOpen(true);
  };

  const handleEditClick = (id) => {
    setEditingItem(organizations.find((org) => org.id === id));
    setIsModalOpen(true);
  };

  const columns = createColumns(handleEditClick, handleAssignClick);

  const fetchOrganizations = async (page, size) => {
    try {
      setIsLoading(true);
      const status = true;
      const search = '';
      const response = await OrganizationService.listAllOrganizations(
        page,
        size,
        status,
        search
      );
      setOrganizations(response.data.items);
      setTotalItems(response.data.pagination.total);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast({
        title: 'Error',
        description: 'Failed to load organizations. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations(currentPage, pageSize);
  }, [currentPage, pageSize]);

  // Initialize URL params if not present
  useEffect(() => {
    if (!searchParams.has('pageSize')) {
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.set('pageSize', DEFAULT_PAGE_SIZE.toString());
      setSearchParams(updatedParams);
    }
  }, []);

  const updateUrlParams = (params) => {
    const updatedParams = new URLSearchParams(searchParams);
    if (params.page) updatedParams.set('page', params.page.toString());
    if (params.pageSize)
      updatedParams.set('pageSize', params.pageSize.toString());
    setSearchParams(updatedParams);
  };

  const handlePageChange = (page) => {
    updateUrlParams({ page });
  };

  const handlePageSizeChange = (newPageSize) => {
    updateUrlParams({ pageSize: newPageSize, page: 1 });
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingItem(null);
  };

  const handleCreate = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleSubmit = async (formData) => {
    try {
      if (editingItem) {
        await updateOrganizationMutation.mutateAsync({
          id: editingItem.id,
          data: formData,
        });
        toast({
          title: 'Success',
          description: 'Organization updated successfully.',
          variant: 'success',
        });
      } else {
        await createOrganizationMutation.mutateAsync(formData);
        toast({
          title: 'Success',
          description: 'Organization created successfully.',
          variant: 'success',
        });
      }
      handleModalClose();
      fetchOrganizations(currentPage, pageSize);
    } catch (error) {
      console.error('Error saving organization:', error);
      toast({
        title: 'Error',
        description: `Failed to ${
          editingItem ? 'update' : 'create'
        } organization.`,
        variant: 'destructive',
      });
    }
  };

  const handleAssignRM = async (organizationId, rmId) => {
    try {
      const payload = {
        rm_id: rmId,
      };
      await assignRMMutation.mutateAsync({
        organizationId,
        data: payload,
      });
      toast({
        title: 'Success',
        description: 'Relationship Manager assigned successfully.',
        variant: 'success',
      });
      fetchOrganizations(currentPage, pageSize);
    } catch (error) {
      console.error('Error assigning RM:', error);
      toast({
        title: 'Error',
        description: 'Failed to assign Relationship Manager.',
        variant: 'destructive',
      });
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const emptyState = (
    <div className="bg-card rounded-xl border border-border shadow-sm flex flex-col items-center justify-center py-16 text-center">
      <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-5">
        <Building2
          className="w-7 h-7 text-muted-foreground"
          strokeWidth={1.5}
        />
      </div>
      <p className="text-[15px] font-semibold text-foreground-strong mb-1.5 tracking-tight">
        No organizations found
      </p>
      <p className="text-[13px] text-muted-foreground max-w-xs mb-5 leading-relaxed">
        Get started by creating your first client organisation.
      </p>
      <button
        type="button"
        onClick={handleCreate}
        className="inline-flex items-center gap-1.5 px-3.5 py-[7px] rounded-[7px] border border-input bg-background text-[12px] font-[550] text-muted-foreground hover:border-primary hover:text-primary hover:bg-brand-bg-subtle transition-all cursor-pointer"
      >
        <Plus className="w-3.5 h-3.5" strokeWidth={2} />
        Create Organization
      </button>
    </div>
  );

  return (
    <div>
      <PageHeader
        title="Organizations"
        actions={
          <button
            type="button"
            onClick={handleCreate}
            className="inline-flex items-center gap-1.5 px-3.5 py-[7px] rounded-[7px] border border-input bg-card text-[12px] font-[550] text-muted-foreground hover:border-primary hover:text-primary hover:bg-brand-bg-subtle transition-[border-color,color,background-color] cursor-pointer"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={2} />
            Create Organization
          </button>
        }
      />

      <PageContainer className="space-y-5">
        <DataTable
          data={organizations}
          columns={columns}
          loading={isLoading}
          tableLayout="auto"
          pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          className="rounded-xl overflow-hidden"
          emptyState={emptyState}
          rowClassName={() =>
            'hover:bg-brand-bg-subtle transition-colors cursor-pointer'
          }
        />
      </PageContainer>

      <Dialog open={isModalOpen} onOpenChange={handleModalClose}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Edit Organization' : 'Create Organization'}
            </DialogTitle>
            <DialogDescription>
              {editingItem
                ? `Editing: ${editingItem.name}`
                : 'Add a new client organisation to the platform.'}
            </DialogDescription>
          </DialogHeader>
          <OrganizationModal
            initialData={editingItem}
            onSubmit={handleSubmit}
            onCancel={handleModalClose}
            loading={
              createOrganizationMutation.isPending ||
              updateOrganizationMutation.isPending
            }
          />
        </DialogContent>
      </Dialog>

      <RMAssignmentModal
        isOpen={isRMModalOpen}
        onClose={() => setIsRMModalOpen(false)}
        organizationId={selectedOrgId}
        onAssign={handleAssignRM}
        initialRMId={
          organizations.find((org) => org.id === selectedOrgId)?.rm_id
        }
      />
    </div>
  );
}

export default Organizations;
